import { Inspection } from '@services';
import { deletePhotoDraft, loadPhotoDrafts, savePhotoDraft } from '@services';
import { useStore } from '@stores';
import { CompressedImage, Utils } from '@utils';
import { message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const MAX_PHOTOS_PER_ITEM = 4;

/** Matches the answer queue: covers a server that is down, which fires no `online` event. */
const RETRY_MS = 30000;

export type InspectionPhotoStatus = 'uploading' | 'done' | 'error';

export interface InspectionPhoto {
  /** Stable for the life of the thumbnail, across upload, failure, reload and retry. */
  key: string;
  subCategoryItemId: number;
  status: InspectionPhotoStatus;
  /** Bytes actually on the wire, not a guess. */
  percent: number;
  name: string;
  /** Object url or data url while it waits, signed url once it is stored. */
  src?: string;
  documentId?: string;
}

export interface IInspectionPhotosHook {
  photosByItem: Map<number, InspectionPhoto[]>;
  uploadingCount: number;
  failedCount: number;
  add: (subCategoryItemId: number, files: File[]) => void;
  remove: (key: string) => void;
  retry: (key: string) => void;
  retryAllFailed: () => void;
}

let photoKeySeed = 0;
const nextKey = () => `photo-${Date.now()}-${(photoKeySeed += 1)}`;

/**
 * Every photo uploads on its own, the moment it is picked, and waits on the device until it does.
 *
 * They used to ride along with the save as base64 - a whole checklist of camera files in one body,
 * which is what tripped the 30s timeout, and one bad file took the rest down with it. One request per
 * photo means a failure costs that photo and nothing else, and there is a real percentage to show
 * instead of a spinner that tells nobody anything.
 *
 * Compression happens once, when the photo is picked rather than when it is sent: the stored copy is
 * then the thing that gets uploaded, so a retry costs nothing extra and what is kept on the device is
 * ~300KB rather than the 4-6MB that came off the camera.
 */
export const useInspectionPhotos = (
  inspectionId: string | undefined,
  inspection: Inspection | undefined
): IInspectionPhotosHook => {
  const { inspectionStore } = useStore();

  const [photos, setPhotos] = useState<InspectionPhoto[]>([]);

  // A mirror of the list that callbacks can read without being rebuilt every time a percentage
  // ticks - upload progress would otherwise churn every handler on the screen.
  const photosRef = useRef<InspectionPhoto[]>([]);
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  // What each waiting photo would send. Held here as well as on the device so a retry in this session
  // does not have to go back to storage.
  const payloadsRef = useRef<Map<string, CompressedImage>>(new Map());
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  const objectUrlsRef = useRef<Set<string>>(new Set());
  const seededForRef = useRef<string | undefined>(undefined);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      // Uploads in flight are deliberately left to finish. Closing the drawer unmounts this, and
      // aborting here would strand a photo that was seconds from landing - it would sit in storage
      // until someone opened that inspection again, which is not what the screen just promised.
      // Anything that does fail still has its copy on the device. Only an explicit remove aborts.
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const patch = useCallback((key: string, changes: Partial<InspectionPhoto>) => {
    if (!mountedRef.current) return;

    setPhotos((current) =>
      current.map((photo) => (photo.key === key ? { ...photo, ...changes } : photo))
    );
  }, []);

  const upload = useCallback(
    async (key: string) => {
      const payload = payloadsRef.current.get(key);
      const photo = photosRef.current.find((candidate) => candidate.key === key);

      if (!inspectionId || !payload || !photo) return;

      // Already going up - the heartbeat and a manual retry can land on the same photo.
      if (controllersRef.current.has(key)) return;

      const controller = new AbortController();
      controllersRef.current.set(key, controller);

      patch(key, { status: 'uploading', percent: 0 });

      try {
        const document = await inspectionStore.uploadResponseDocument(
          {
            inspectionId,
            subCategoryItemId: photo.subCategoryItemId,
            name: payload.name,
            contentType: payload.contentType,
            content: payload.content,
          },
          {
            signal: controller.signal,
            onProgress: (percent) => patch(key, { percent }),
          }
        );

        // Only once the server has it.
        payloadsRef.current.delete(key);
        void deletePhotoDraft(key);

        // Removed while it was going up: the abort came too late to stop the request, so the server
        // now holds a photo the user has already thrown away. Without this it would be gone from the
        // screen and back again on the next open.
        if (!photosRef.current.some((candidate) => candidate.key === key)) {
          void inspectionStore.deleteResponseDocument(document.id).catch(() => undefined);
          return;
        }

        patch(key, {
          status: 'done',
          percent: 100,
          documentId: document.id,
          src: document.signedUrl ?? document.url,
        });
      } catch {
        // An abort is us removing the thumbnail; the row is already gone.
        if (controller.signal.aborted) return;

        patch(key, { status: 'error' });
      } finally {
        controllersRef.current.delete(key);
      }
    },
    [inspectionId, inspectionStore, patch]
  );

  /**
   * One pass over everything still waiting. Used by the retry button, by the connection coming back,
   * and by the heartbeat.
   */
  const uploadPending = useCallback(() => {
    photosRef.current
      .filter((photo) => photo.status === 'error' && payloadsRef.current.has(photo.key))
      .forEach((photo) => void upload(photo.key));
  }, [upload]);

  // Seed once per inspection: what the server already holds, plus anything still waiting on this
  // device from an earlier session. Both in one write, so neither can wipe the other.
  useEffect(() => {
    if (!inspectionId) {
      seededForRef.current = undefined;
      setPhotos([]);
      return;
    }

    if (!inspection || seededForRef.current === inspection.id) return;

    seededForRef.current = inspection.id;

    let cancelled = false;

    const stored = (inspection.responses ?? []).flatMap((response) =>
      (response.documents ?? []).map((document) => ({
        key: nextKey(),
        subCategoryItemId: response.subCategoryItemId,
        status: 'done' as const,
        percent: 100,
        name: document.name,
        src: document.signedUrl ?? document.url,
        documentId: document.id,
      }))
    );

    loadPhotoDrafts(inspectionId).then((drafts) => {
      if (cancelled || !mountedRef.current) return;

      const waiting = drafts.map((draft) => {
        payloadsRef.current.set(draft.key, {
          name: draft.name,
          contentType: draft.contentType,
          content: draft.content,
        });

        return {
          key: draft.key,
          subCategoryItemId: draft.subCategoryItemId,
          status: 'error' as const,
          percent: 0,
          name: draft.name,
          src: `data:${draft.contentType};base64,${draft.content}`,
        };
      });

      setPhotos([...stored, ...waiting]);
      photosRef.current = [...stored, ...waiting];

      // Anything recovered goes straight back up if there is a connection to do it over.
      if (waiting.length > 0) uploadPending();
    });

    return () => {
      cancelled = true;
    };
  }, [inspectionId, inspection, uploadPending]);

  const add = useCallback(
    (subCategoryItemId: number, files: File[]) => {
      if (!inspectionId) return;

      const alreadyOnItem = photosRef.current.filter(
        (photo) => photo.subCategoryItemId === subCategoryItemId
      ).length;
      const room = MAX_PHOTOS_PER_ITEM - alreadyOnItem;

      if (room <= 0) {
        message.warning(`Up to ${MAX_PHOTOS_PER_ITEM} photos per item.`);
        return;
      }

      // Say so rather than dropping the extras quietly, which is what used to happen.
      if (files.length > room)
        message.warning(
          `Only ${room} more photo${room === 1 ? '' : 's'} fit on this item - the rest were skipped.`
        );

      const accepted = files.slice(0, room).map((file) => {
        const key = nextKey();
        const objectUrl = URL.createObjectURL(file);

        objectUrlsRef.current.add(objectUrl);

        return {
          photo: {
            key,
            subCategoryItemId,
            status: 'uploading' as const,
            percent: 0,
            name: file.name,
            src: objectUrl,
          },
          file,
        };
      });

      // The mirror is advanced here rather than waiting for the render, so picking twice before React
      // catches up still counts against the limit.
      photosRef.current = [...photosRef.current, ...accepted.map((item) => item.photo)];
      setPhotos((current) => [...current, ...accepted.map((item) => item.photo)]);

      accepted.forEach(async ({ photo, file }) => {
        try {
          const compressed = await Utils.compressImage(file);

          payloadsRef.current.set(photo.key, compressed);

          // On the device before the first attempt, so a photo survives the tab closing between
          // being taken and reaching the server.
          await savePhotoDraft({
            key: photo.key,
            inspectionId,
            subCategoryItemId: photo.subCategoryItemId,
            name: compressed.name,
            contentType: compressed.contentType,
            content: compressed.content,
          });

          void upload(photo.key);
        } catch {
          // Nothing to send and nothing to retry from, so a failed tile would sit there red with a
          // retry button that could never do anything. Take it away and say why.
          photosRef.current = photosRef.current.filter(
            (candidate) => candidate.key !== photo.key
          );
          setPhotos((current) => current.filter((candidate) => candidate.key !== photo.key));

          message.error(`${file.name} could not be read.`);
        }
      });
    },
    [inspectionId, patch, upload]
  );

  const remove = useCallback(
    async (key: string) => {
      const photo = photosRef.current.find((candidate) => candidate.key === key);
      if (!photo) return;

      controllersRef.current.get(key)?.abort();
      controllersRef.current.delete(key);
      payloadsRef.current.delete(key);
      void deletePhotoDraft(key);

      // Mirror first, so an upload finishing in the same tick can see it has been removed.
      photosRef.current = photosRef.current.filter((candidate) => candidate.key !== key);
      setPhotos((current) => current.filter((candidate) => candidate.key !== key));

      if (!photo.documentId) return;

      try {
        await inspectionStore.deleteResponseDocument(photo.documentId);
      } catch {
        // It is still on the server; put it back rather than pretend it is gone.
        mountedRef.current && setPhotos((current) => [...current, photo]);
      }
    },
    [inspectionStore]
  );

  const retry = useCallback((key: string) => void upload(key), [upload]);

  // Same pair as the answer queue: the event for an interface coming back, the heartbeat for
  // everything the browser does not announce.
  useEffect(() => {
    window.addEventListener('online', uploadPending);
    const heartbeat = setInterval(uploadPending, RETRY_MS);

    return () => {
      window.removeEventListener('online', uploadPending);
      clearInterval(heartbeat);
    };
  }, [uploadPending]);

  const photosByItem = useMemo(() => {
    const grouped = new Map<number, InspectionPhoto[]>();

    photos.forEach((photo) => {
      const existing = grouped.get(photo.subCategoryItemId);
      existing ? existing.push(photo) : grouped.set(photo.subCategoryItemId, [photo]);
    });

    return grouped;
  }, [photos]);

  return {
    photosByItem,
    uploadingCount: photos.filter((photo) => photo.status === 'uploading').length,
    failedCount: photos.filter((photo) => photo.status === 'error').length,
    add,
    remove: (key: string) => void remove(key),
    retry,
    retryAllFailed: uploadPending,
  };
};
